import React from 'react';
import styled from 'styled-components';

import { InputType } from '../../../shared/constants';

import CheckIcon from '../../atoms/icons/CheckIcon';
import InfoPopover from '../../atoms/info-popover/InfoPopover';
import Textarea from '../../atoms/text-area/Textarea';

import { ReactMarkdownExtended } from '../../../utils/generic';
import { languageHelper } from '../../../utils/language';
import { TEXT_AREA_TRANSLATIONS } from '../../../utils/language/wizard-labels';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';
import { Theme } from '../../../styles/themes';

import { WizardItem } from '../../../types/wizard';

interface WizardCheckboxProps extends WizardItem {
  isChecked: boolean;
  onChange: (id: WizardItem['id'], isChecked: boolean) => void;
  onTextInput: (id: WizardItem['id'], value: string) => void;
}

const WizardCheckbox = ({
  id,
  title,
  isChecked,
  visual,
  onChange,
  onTextInput,
}: WizardCheckboxProps) => {
  const { itemsType, image, tooltip } = visual || {};
  const { lang } = languageHelper();

  const handleTextAreaInput = (value: string) => {
    onTextInput(id, value);
  };

  return (
    <div>
      <StyledLabel
        isChecked={isChecked}
        data-testid="wizard-checkbox"
        htmlFor={id}
      >
        <StyledInputWrapper>
          <StyledInput
            id={id}
            type="checkbox"
            data-testid="hidden-checkbox"
            checked={isChecked}
            onChange={(event) => onChange(id, event.target.checked)}
          />
          <StyledCheckmark isChecked={isChecked}>
            <StyledIcon isChecked={isChecked} color={Colors.white} />
          </StyledCheckmark>
          {image?.file && (
            <StyledImage alt="Checkbox icon" src={image.file.url} />
          )}
          <Body data-testid="label">{title}</Body>
        </StyledInputWrapper>
        {tooltip && (
          <StyledInfoPopover label={title || ''}>
            <ReactMarkdownExtended>
              {tooltip.childMarkdownRemark?.rawMarkdownBody}
            </ReactMarkdownExtended>
          </StyledInfoPopover>
        )}
      </StyledLabel>
      {itemsType === InputType.TEXT_INPUT_CHECKBOX && isChecked && (
        <StyledTextarea
          id="text-value"
          name="text-value"
          label={TEXT_AREA_TRANSLATIONS.label[lang]}
          errorText={TEXT_AREA_TRANSLATIONS.errorLabel}
          onInput={handleTextAreaInput}
          required
        />
      )}
    </div>
  );
};

interface CheckedProps {
  isChecked: boolean;
}

const StyledInputWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const StyledInput = styled.input`
  display: none;
`;

const StyledLabel = styled.label<CheckedProps>`
  align-items: center;
  background-color: ${({ isChecked }) =>
    isChecked ? Colors.white : Colors.abnGreen50};
  border: 2px solid
    ${({ isChecked, theme }) =>
      isChecked ? theme.colors.abnGreen300 : Colors.abnGreen50};
  border-radius: ${({ theme }) => theme.radiuses.S};

  &:active {
    background-color: ${({ isChecked }) =>
      isChecked ? Colors.abnGreen65 : Colors.abnGreen55};
    border: 2px solid ${({ theme }) => theme.colors.abnGreen100};
  }

  &:hover {
    background-color: ${Colors.abnGreen55};
    border: 2px solid
      ${({ isChecked, theme }: { isChecked: boolean; theme: Theme }) =>
        isChecked ? theme.colors.abnGreen300 : Colors.abnGreen75};
  }
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacings.XXS};
  padding: 12px 12px 12px ${Spacings.S};

  &:hover {
    cursor: pointer;
  }
`;

const StyledCheckmark = styled.div<CheckedProps>`
  align-items: center;
  background: ${({ isChecked }) =>
    isChecked ? Colors.abnGreen200 : Colors.white};
  border: 1px solid
    ${({ isChecked }) =>
      isChecked ? Colors.abnGreen200 : Colors.abnNeutralsN100};
  border-radius: ${({ theme }) => theme.radiuses.S};
  box-sizing: border-box;
  display: flex;
  height: ${Spacings.S};
  justify-content: center;
  padding: 1px;
  transition: all 150ms;
  width: ${Spacings.S};
`;

const StyledTextarea = styled(Textarea)`
  margin-top: ${Spacings.S};
  width: 100%;

  textarea {
    width: 100%;
  }
`;

const StyledIcon = styled(CheckIcon)<CheckedProps>`
  height: ${Spacings.S};
  visibility: ${({ isChecked }) => (isChecked ? 'visible' : 'hidden')};
  width: ${Spacings.S};
`;

const StyledImage = styled.img`
  height: 48px;
  width: 48px;
`;

const StyledInfoPopover = styled(InfoPopover)`
  margin-right: 12px;

  ${mq.from.breakpoint.M`
    margin-right: 20px;
  `}
`;

export default WizardCheckbox;
